import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { TailSpin } from 'react-loading-icons'
import { toast } from 'sonner'

import AssigneesFilter from 'components/brokers/needs_attention/AssigneesFilter'
import { setQueryDataCallback } from 'components/brokers/needs_attention/utils'
import { Banner } from 'components/reusable/Banner'
import OpacityTransition from 'components/reusable/OpacityTransition'
import { Button } from 'components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { getGetAdminEmployersQueryKey, usePatchAdminEmployers } from 'utils/web_api'
import { type GetAdminEmployersParams } from 'utils/web_api.schemas'

export type BulkUpdateEmployerReviewsDialogProps = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  selectedItems: string[]
  onSuccess?: () => void
  params?: GetAdminEmployersParams
}

type BulkUpdateEmployerReviewsFormInput = {
  broker_ids_to_add: string[]
  broker_ids_to_remove: string[]
}

const BulkUpdateEmployerReviewsDialog = ({
  isOpen,
  setIsOpen,
  selectedItems,
  onSuccess,
  params,
}: BulkUpdateEmployerReviewsDialogProps) => {
  const queryClient = useQueryClient()

  const [assigneesValidationError, setAssigneesValidationError] =
    React.useState<string | null>()

  const { handleSubmit, setValue, watch, reset } =
    useForm<BulkUpdateEmployerReviewsFormInput>({
      defaultValues: {
        broker_ids_to_add: [],
        broker_ids_to_remove: [],
      },
    })

  const toggleState = (
    key: keyof BulkUpdateEmployerReviewsFormInput,
    value: string
  ) => {
    const currentValue = watch(key)
    if (currentValue.includes(value)) {
      setValue(
        key,
        currentValue.filter((v) => v !== value)
      )
    } else {
      setValue(key, [...currentValue, value])
    }
  }

  const assigneesToAdd = watch('broker_ids_to_add')
  const assigneesToRemove = watch('broker_ids_to_remove')

  const handleOnOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      reset()
      setAssigneesValidationError(null)
    }
  }

  useEffect(() => {
    const conflict = assigneesToAdd.find((assignee) =>
      assigneesToRemove.includes(assignee)
    )

    if (conflict) {
      setAssigneesValidationError(
        'Assignees cannot be both added and removed. Please remove duplicate assignees.'
      )
    } else {
      setAssigneesValidationError(null)
    }
  }, [assigneesToAdd, assigneesToRemove])

  const patchEmployersMutation = usePatchAdminEmployers()

  const onSubmit: SubmitHandler<BulkUpdateEmployerReviewsFormInput> = (
    data
  ) => {
    patchEmployersMutation.mutate(
      {
        data: {
          employer_ids: selectedItems,
          ...data,
        },
      },
      {
        onSuccess: (data) => {
          // Update cached data with new employer items that include updated broker assignments.
          if (params) {
            queryClient.setQueryData(
              getGetAdminEmployersQueryKey(params),
              setQueryDataCallback(data)
            )
          }
          toast.success(`${selectedItems.length} items successfully updated!`, {
            position: 'top-center',
          })
          onSuccess?.()
          handleOnOpenChange(false)
        },
      }
    )
  }

  const isValidRequest =
    (assigneesToAdd.length > 0 || assigneesToRemove.length > 0) &&
    !assigneesValidationError

  return (
    <form>
      <Dialog open={isOpen} onOpenChange={handleOnOpenChange}>
        <DialogContent className='flex flex-col max-h-[90vh] p-0'>
          <DialogHeader className='p-6'>
            <DialogTitle className='text-xl'>
              Update Selected Employers
            </DialogTitle>
            <DialogDescription>
              {selectedItems.length} items selected
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-2 flex-1 overflow-y-auto p-6'>
            <div className='font-bold text-lg'>Assignees</div>
            <div className='font-semibold text-ds-text-secondary'>
              Add assignees:
            </div>
            <AssigneesFilter
              selectedValues={assigneesToAdd}
              onSelectValue={(value) => toggleState('broker_ids_to_add', value)}
            />
            <div className='font-semibold text-ds-text-secondary'>
              Remove assignees:
            </div>
            <AssigneesFilter
              selectedValues={assigneesToRemove}
              onSelectValue={(value) =>
                toggleState('broker_ids_to_remove', value)}
            />
            {assigneesValidationError && (
              <OpacityTransition>
                <Banner
                  type='critical'
                  titleText='Validation error'
                  className='mt-2'
                >
                  {assigneesValidationError}
                </Banner>
              </OpacityTransition>
            )}
          </div>

          <DialogFooter className='flex items-center justify-end mt-4 p-6'>
            <Button
              type='reset'
              onClick={() => handleOnOpenChange(false)}
              variant='secondary'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={!isValidRequest || patchEmployersMutation.isPending}
              onClick={() => handleSubmit(onSubmit)()}
            >
              {patchEmployersMutation.isPending
                ? (
                  <div className='flex items-center h-[100%]'>
                    <span>Updating...</span>
                    <TailSpin fill='#FFF' stroke='#FFF' className='h-[90%]' />
                  </div>
                  )
                : (
                    'Update'
                  )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  )
}

export default BulkUpdateEmployerReviewsDialog
